import { ComponentType, lazy, LazyExoticComponent, Suspense } from 'react';
import { isIOS } from 'react-device-detect';
import { createBrowserRouter } from 'react-router-dom';
import { environment } from 'env/cashier';

import {
  convertToLocale,
  initI18n,
  initiateQAToolkit,
} from '@cashier/cashier-common';
import {
  fetchCertainPaymentMethod,
  initialiseCashierConfiguration,
} from '@cashier/cashier-data-access';
import {
  CashierConfigurationKeys,
  ColourThemes,
  Directions,
  PaymentTypes,
  SubmitInitiators,
  Versions,
} from '@cashier/cashier-model';

const UpdateAppPage = lazy(
  () => import('../pages/updateAppPage/updateAppPage'),
);
const MainPageV1 = lazy(() => import('../pages/main/v1/main'));
const MainPageV2 = lazy(() => import('../pages/main/v2/main'));
const MainPageV4 = lazy(() => import('../pages/main/v4/main'));
const LoadingPage = lazy(() => import('../pages/loading/loading'));
const ConfirmationPage = lazy(
  () => import('../pages/confirmation/confirmation'),
);
const wrapperWithSuspense = (Component: LazyExoticComponent<ComponentType>) => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

export const getAppRouter = (store) => {
  initiateQAToolkit();

  const loadTranslations = () => {
    initI18n(environment.paymentGatewayApiUrl);
  };

  const loadPaymentMethod = (paymentMethodId: string) => {
    const queryParams = new URLSearchParams(window.location.search);
    const activeType = queryParams.get(
      CashierConfigurationKeys.PaymentType,
    ) as PaymentTypes;
    const bonusCode = queryParams.get(CashierConfigurationKeys.Bonus);

    if (activeType) {
      store.dispatch(
        fetchCertainPaymentMethod({
          type: activeType,
          id: paymentMethodId,
          bonusCode,
        }),
      );
    }
  };

  interface DefaultCashierConfiguration {
    version: 'v1' | 'v2' | 'v3' | 'v4';
    notifyCustomerAboutPaymentResult?: boolean;
    submitInitiator?: string;
    activeType?: string;
  }

  const prepareCashierConfiguration = (
    defaultConfiguration: DefaultCashierConfiguration,
  ) => {
    const queryParams = new URLSearchParams(window.location.search);

    const {
      notifyCustomerAboutPaymentResult,
      submitInitiator,
      activeType,
      version,
    } = defaultConfiguration || {};

    const getParam = (
      key: CashierConfigurationKeys,
      defaultValue?: string,
    ): string => {
      return queryParams.get(key.toString()) || defaultValue || null;
    };

    store.dispatch(
      initialiseCashierConfiguration({
        activeType: activeType
          ? activeType
          : getParam(CashierConfigurationKeys.PaymentType)
            ? (getParam(CashierConfigurationKeys.PaymentType) as PaymentTypes)
            : null,
        accountId: getParam(CashierConfigurationKeys.AccountId),
        bonusCode: getParam(CashierConfigurationKeys.Bonus),
        currency: getParam(CashierConfigurationKeys.Currency),
        hostUrl: getParam(CashierConfigurationKeys.Host),
        language: convertToLocale(getParam(CashierConfigurationKeys.Language)),
        merchantId: getParam(CashierConfigurationKeys.MerchantId),
        pgwMerchantId: getParam(CashierConfigurationKeys.PgwMerchantId),
        platform: getParam(CashierConfigurationKeys.Platform),
        sessionId: getParam(CashierConfigurationKeys.SessionId),
        termsUrl: getParam(CashierConfigurationKeys.TermsUrl),
        userId: getParam(CashierConfigurationKeys.UserId),
        cashierHostLocation: window.location.origin,
        customerBalance: getParam(CashierConfigurationKeys.CustomerBalance),
        webView: 'true' === getParam(CashierConfigurationKeys.WebView),
        direction: getParam(
          CashierConfigurationKeys.Direction,
          Directions.Ltr,
        ) as Directions,
        country: getParam(CashierConfigurationKeys.Country),
        orderId:
          getParam(CashierConfigurationKeys.OrderId) &&
          getParam(CashierConfigurationKeys.OrderId) !== 'null'
            ? getParam(CashierConfigurationKeys.OrderId)
            : '',
        amount:
          getParam(CashierConfigurationKeys.Amount) &&
          getParam(CashierConfigurationKeys.Amount) !== 'null'
            ? getParam(CashierConfigurationKeys.Amount)
            : '',
        initialAmount: getParam(CashierConfigurationKeys.InitialAmount),
        theme: getParam(
          CashierConfigurationKeys.Theme,
          ColourThemes.Dark,
        ) as ColourThemes,
        submitInitiator: submitInitiator
          ? (submitInitiator as SubmitInitiators)
          : (getParam(
              CashierConfigurationKeys.SubmitInitiator,
              SubmitInitiators.Self,
            ) as SubmitInitiators),
        notifyCustomerAboutPaymentResult,
        version: version as Versions,
      }),
    );
  };

  return createBrowserRouter([
    {
      path: '/',
      element: wrapperWithSuspense(isIOS ? UpdateAppPage : MainPageV1),
      loader: () => {
        loadTranslations();
        prepareCashierConfiguration({ version: 'v1' });
        return null;
      },
    },
    {
      path: '/v1',
      element: wrapperWithSuspense(isIOS ? UpdateAppPage : MainPageV1),
      loader: () => {
        loadTranslations();
        prepareCashierConfiguration({ version: 'v1' });
        return null;
      },
    },
    {
      path: '/:theme/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV2),
      loader: ({ params }) => {
        loadPaymentMethod(params.paymentMethodId);
        loadTranslations();
        const queryParams = new URLSearchParams(window.location.search);
        const submitInitiator = (queryParams.get(
          CashierConfigurationKeys.SubmitInitiator,
        ) || SubmitInitiators.Self) as SubmitInitiators;

        prepareCashierConfiguration({
          version: submitInitiator === SubmitInitiators.Parent ? 'v3' : 'v2',
        });
        return null;
      },
    },
    {
      path: '/v2/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV2),
      loader: ({ params }) => {
        loadPaymentMethod(params.paymentMethodId);
        loadTranslations();
        prepareCashierConfiguration({ version: 'v2' });
        return null;
      },
    },
    {
      path: '/v4/:paymentMethodId',
      element: wrapperWithSuspense(MainPageV4),
      loader: ({ params }) => {
        loadPaymentMethod(params.paymentMethodId);
        loadTranslations();
        prepareCashierConfiguration({
          notifyCustomerAboutPaymentResult: true,
          submitInitiator: SubmitInitiators.Self,
          activeType: PaymentTypes.Deposit,
          version: 'v4',
        });
        return null;
      },
    },
    {
      path: '/confirm/:id',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
    {
      path: '/confirm',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
    {
      path: '/success',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
    {
      path: '/failure',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
    {
      path: '/status/:merchantId/:id/:paymentType',
      element: wrapperWithSuspense(ConfirmationPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
    {
      path: '/loading',
      element: wrapperWithSuspense(LoadingPage),
      loader: () => {
        loadTranslations();
        return null;
      },
    },
  ]);
};
